import { mutationNamespace, queryNamespace } from '@libs/shared/frontend/helpers/query-namespace.helper';
import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { client, HttpException, type components, type operations } from '@/libs/openapi';
import { queryClient } from '@/main';
import { auditLogQueries } from '@/resources/audit-log/audit-log.api';
import { campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { fromApiCampaignRequestProgramFieldAdapter } from '@/resources/campaign-request-program/campaign-request-program.field-adapter';
import { organizationUserQueries } from '@/resources/organization-user/organization-user.api';
import { fromApiUserFieldAdapter } from '@/resources/user/user.field-adapter';
import type { QueryFnResult, QueryResultData } from '@/types/query-result-data.type';
import { Resource } from '@/types/resource.type';

type CampaignRequestProgramDetailRaw = QueryFnResult<typeof campaignRequestProgramQueries.detail>;
type CampaignRequestListRaw = QueryFnResult<typeof campaignRequestQueries.list>;

export const campaignRequestProgramQueries = queryNamespace(Resource.campaignRequestProgram, {
  list: (params?: {
    filter?: Omit<operations['CampaignRequestProgramController_getMany']['parameters']['query'], 'limit' | 'page' | 'sortBy'>;
    sorting?: {
      id: string;
      desc: boolean;
    };
    paging: {
      pageIndex: number;
      pageSize: number;
    };
  }) => queryOptions({
    queryKey: [ params ],
    queryFn: async ({ signal }) =>
      client
        .GET('/customer/api/tcd/programs', {
          signal,
          params: {
            query: {
              limit: params?.paging?.pageSize ?? 10,
              page: (params?.paging?.pageIndex ?? 0) + 1,
              sortBy: params?.sorting
                ? [ `${params.sorting.id}:${params.sorting.desc ? 'DESC' : 'ASC'}` ]
                : undefined,
              ...params?.filter,
            },
          },
        })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return {
            data: data.items,
            totalCount: data.totalCount,
          };
        }),
    select: (data) => ({
      totalCount: data.totalCount,
      data: data.data.map((row) => ({
        original: row,
        transformed: {
          forLabels: fromApiCampaignRequestProgramFieldAdapter.transform(row),
        },
        meta: {
          ownerFullName: row.owner ? fromApiUserFieldAdapter.getFullName(row.owner) : undefined,
          startDateRelativeToTimezone: fromApiCampaignRequestProgramFieldAdapter.getStartDateRelativeToTimezone(row),
          endDateRelativeToTimezone: fromApiCampaignRequestProgramFieldAdapter.getEndDateRelativeToTimezone(row),
        },
      })),
    }),
  }),
  detail: (id: number) => queryOptions({
    queryKey: [ id ],
    queryFn: async ({ signal }) =>
      client
        .GET('/customer/api/tcd/programs/{id}', {
          signal,
          params: { path: { id }},
        })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return data;
        }),
    select: (data) => ({
      original: data,
      transformed: {
        forLabels: fromApiCampaignRequestProgramFieldAdapter.transform(data),
        forForm: fromApiCampaignRequestProgramFieldAdapter.transform(data, 'forFormValue'),
      },
      meta: {
        ownerFullName: data.owner ? fromApiUserFieldAdapter.getFullName(data.owner) : undefined,
        createdByFullName: data.createdByOrganizationUser ? fromApiUserFieldAdapter.getFullName(data.createdByOrganizationUser) : 'Account manager',
        startDateRelativeToTimezone: fromApiCampaignRequestProgramFieldAdapter.getStartDateRelativeToTimezone(data),
        endDateRelativeToTimezone: fromApiCampaignRequestProgramFieldAdapter.getEndDateRelativeToTimezone(data),
      },
    }),
  }),
  checkDuplicate: (params: { field: 'name'; value: string; excludeId?: number }) =>
    queryOptions({
      queryKey: [ params ],
      queryFn: ({ signal }) =>
        client
          .GET('/customer/api/tcd/programs/check-duplicate', {
            signal,
            params: { query: params },
          })
          .then(({ data, error, response }) => {
            if (error) {
              throw new HttpException({
                message: Array.isArray(error.message) ? error.message[0] : error.message,
                response,
                isGlobal: false,
              });
            }
            return data;
          }),
    }),
});

export type CampaignRequestProgramListQueryFnResult = QueryFnResult<typeof campaignRequestProgramQueries.list>;
export type CampaignRequestProgramListQueryResultData = QueryResultData<typeof campaignRequestProgramQueries.list>['data'][number];

export const campaignRequestProgramMutations = mutationNamespace(Resource.campaignRequestProgram, {
  create: mutationOptions({
    mutationFn: (body: components['schemas']['CreateCampaignRequestProgramDto']) =>
      client
        .POST('/customer/api/tcd/programs', { body })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return data;
        }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignRequestProgramQueries.all.queryKey });
    },
  }),

  update: mutationOptions({
    mutationFn: ({ id, body }: { id: number; body: components['schemas']['UpdateCampaignRequestProgramDto'] }) =>
      client
        .PATCH('/customer/api/tcd/programs/{id}', { params: { path: { id }}, body })
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
        }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignRequestProgramQueries.all.queryKey });
      queryClient.invalidateQueries({ queryKey: auditLogQueries.all.queryKey });
    },
  }),

  updateOptimistic: mutationOptions({
    mutationFn: ({ id, body }: { id: number; body: components['schemas']['UpdateCampaignRequestProgramDto'] }) =>
      client
        .PATCH('/customer/api/tcd/programs/{id}', { params: { path: { id }}, body })
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: true,
            });
          }
        }),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: campaignRequestProgramQueries.detail(id).queryKey });

      const previousDetail = queryClient.getQueryData<CampaignRequestProgramDetailRaw>(campaignRequestProgramQueries.detail(id).queryKey);

      queryClient.setQueryData<CampaignRequestProgramDetailRaw>(
        campaignRequestProgramQueries.detail(id).queryKey,
        (old) => {
          if (!old) {
            return old;
          }

          const updated = { ...old };

          if ('agency' in body) {
            updated.agency = body.agency;
          }

          if ('ownerUserId' in body) {
            if (body.ownerUserId == null) {
              updated.owner = undefined;
            } else {
              const allUserLists = queryClient.getQueriesData<QueryFnResult<typeof organizationUserQueries.list>>(
                { queryKey: organizationUserQueries.all.queryKey },
              );
              const cachedUser = allUserLists
                .flatMap(([ , data ]) => data?.items ?? [])
                .find((u) => u.id === body.ownerUserId);

              if (cachedUser) {
                updated.owner = cachedUser;
              }
            }
          }

          return updated;
        },
      );

      return { previousDetail };
    },
    onError: (_error, { id }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData<CampaignRequestProgramDetailRaw>(campaignRequestProgramQueries.detail(id).queryKey, context.previousDetail);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignRequestProgramQueries.all.queryKey });
      queryClient.invalidateQueries({ queryKey: auditLogQueries.all.queryKey });
    },
  }),

  changeStage: mutationOptions({
    mutationFn: ({ id, body }: { id: number; body: components['schemas']['ChangeCampaignRequestProgramStageDto'] }) =>
      client
        .POST('/customer/api/tcd/programs/{id}/stage', {
          params: { path: { id }},
          body,
        })
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: true,
            });
          }
        }),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: campaignRequestProgramQueries.detail(id).queryKey });

      const previousDetail = queryClient.getQueryData<CampaignRequestProgramDetailRaw>(campaignRequestProgramQueries.detail(id).queryKey);

      queryClient.setQueryData<CampaignRequestProgramDetailRaw>(
        campaignRequestProgramQueries.detail(id).queryKey,
        (old) => old && ({ ...old, stage: body.stage, stageTransitions: [] }),
      );

      return { previousDetail };
    },
    onError: (_error, { id }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData<CampaignRequestProgramDetailRaw>(campaignRequestProgramQueries.detail(id).queryKey, context.previousDetail);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignRequestProgramQueries.all.queryKey });
      queryClient.invalidateQueries({ queryKey: auditLogQueries.all.queryKey });
    },
  }),

  linkCampaignRequest: mutationOptions({
    mutationFn: ({ id, campaignRequestId }: { id: number; campaignRequestId: number }) =>
      client
        .PUT('/customer/api/tcd/programs/{id}/campaign-requests/{campaignRequestId}', {
          params: { path: { id, campaignRequestId }},
        })
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
        }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignRequestProgramQueries.all.queryKey });
      queryClient.invalidateQueries({ queryKey: campaignRequestQueries.all.queryKey });
    },
  }),

  unlinkCampaignRequest: mutationOptions({
    mutationFn: ({ id, campaignRequestId }: { id: number; campaignRequestId: number }) =>
      client
        .DELETE('/customer/api/tcd/programs/{id}/campaign-requests/{campaignRequestId}', {
          params: { path: { id, campaignRequestId }},
        })
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: true,
            });
          }
        }),
    onMutate: async ({ campaignRequestId }) => {
      await queryClient.cancelQueries({ queryKey: campaignRequestQueries.all.queryKey });

      const previousLists = queryClient.getQueriesData<CampaignRequestListRaw>(
        { queryKey: campaignRequestQueries.all.queryKey },
      );

      queryClient.setQueriesData<CampaignRequestListRaw>(
        { queryKey: campaignRequestQueries.all.queryKey },
        (old) => (old?.data != null ? {
          ...old,
          data: old.data.filter((row) => row.id !== campaignRequestId),
          totalCount: Math.max(0, old.totalCount - 1),
        } : old),
      );

      return { previousLists };
    },
    onError: (_error, _variables, context) => {
      context?.previousLists?.forEach(([ queryKey, data ]) => queryClient.setQueryData(queryKey, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignRequestProgramQueries.all.queryKey });
      queryClient.invalidateQueries({ queryKey: campaignRequestQueries.all.queryKey });
    },
  }),
});
